import { jsPDF } from "jspdf";
import { logoBase64 } from "@/utils/pdfLogo";
import { eSignatureLee, eSignatureMelanie } from "@/utils/eSignatureDoctors.js";

import moment from "moment";

export default function pdfReferralGenerateHandler() {
  const safeText = (value) => (value ? String(value).trim() : "");

  const exportReferralPDF = async (item, detail) => {
    if (!item || !item.length) {
      console.error("❌ Invalid data: 'item' is empty or undefined.");
      return;
    }

    const doc = new jsPDF({
      unit: "mm",
      format: "a4",
      compress: true,
    });

    const pageWidth = doc.internal.pageSize.getWidth();

    const pageHeight = doc.internal.pageSize.getHeight();

    const bottomMargin = 20;

    // === HEADER ===
    const addHeader = () => {
      const logoWidth = 90;
      const logoHeight = 22;

      const logoX = (pageWidth - logoWidth) / 2;

      try {
        doc.addImage(
          logoBase64,
          "JPEG",
          logoX,
          10,
          logoWidth,
          logoHeight,
          "",
          "FAST",
        );
      } catch (err) {
        console.warn("⚠️ Logo could not be added:", err);
      }

      doc.setFont("helvetica", "normal");

      doc.setFontSize(13);

      doc.text(safeText(item[0].doctor_name), pageWidth / 2, 40, {
        align: "center",
      });

      doc.setFontSize(11);

      doc.text(safeText(item[0].specialization), pageWidth / 2, 46, {
        align: "center",
      });

      doc.text(`Patient Name: ${safeText(item[0].membername)}`, 10, 63);

      doc.text(
        `Date: ${
          moment(detail.createdat).isValid()
            ? moment(detail.createdat).format("LL")
            : moment().format("LL")
        }`,
        pageWidth - 10,
        63,
        {
          align: "right",
        },
      );

      doc.text(
        `Birthdate: ${
          moment(item[0].birthdate).isValid()
            ? moment(item[0].birthdate).format("LL")
            : "N/A"
        }`,
        10,
        71,
      );

      doc.setFont("helvetica", "bold");

      doc.setFontSize(32);

      doc.text("R", 10, 92);

      doc.setFontSize(28);

      doc.text("x", 18, 92);
    };

    const addContinuationHeader = () => {
      doc.setFont("helvetica", "bold");

      doc.setFontSize(14);

      doc.text("TEST PROCEDURES CONTINUATION", pageWidth / 2, 25, {
        align: "center",
      });

      doc.setDrawColor(150);

      doc.setLineWidth(0.3);

      doc.line(10, 30, pageWidth - 10, 30);
    };

    const addFooter = () => {
      const currentPage = doc.internal.getNumberOfPages();

      doc.setFont("helvetica", "italic");

      doc.setFontSize(10);

      doc.setTextColor(100);

      doc.text(`Page ${currentPage}`, pageWidth / 2, pageHeight - 10, {
        align: "center",
      });
    };

    addHeader();

    // === GROUP PROCEDURES ===
    const groupedItems = item.reduce((acc, curr) => {
      const cat = safeText(curr.category);

      if (!cat) return acc;

      if (!acc[cat]) acc[cat] = [];

      acc[cat].push(curr);

      return acc;
    }, {});

    let y = 105;

    const addNewPage = () => {
      addFooter();

      doc.addPage();

      addContinuationHeader();

      y = 45;
    };

    for (const [category, procedures] of Object.entries(groupedItems)) {
      if (y + 20 > pageHeight - bottomMargin) {
        addNewPage();
      }

      doc.setFont("helvetica", "bold");

      doc.setFontSize(12);

      doc.text(safeText(category), 25, y);

      y += 5;

      doc.setFont("helvetica", "normal");

      doc.setFontSize(11);

      procedures.forEach((pres) => {
        if (y + 8 > pageHeight - bottomMargin) {
          addNewPage();
        }

        doc.text(`• ${safeText(pres.procedures)}`, 30, y);

        y += 6;
      });

      y += 3;
    }

    // === SECTIONS ===
    const addSection = (label, text) => {
      const cleanText = safeText(text);

      if (!cleanText) return;

      if (y + 25 > pageHeight - bottomMargin) {
        addNewPage();
      }

      doc.setFont("helvetica", "bold");

      doc.setFontSize(12);

      doc.text(`${label}:`, 15, y);

      doc.setFont("helvetica", "normal");

      doc.setFontSize(11);

      const splitText = doc.splitTextToSize(cleanText, pageWidth - 30);

      doc.text(splitText, 30, y + 5);

      y += splitText.length * 5.5 + 8;
    };

    addSection("Remarks", item[0].remarks);

    addSection(
      "Follow-up Date",
      moment(item[0].followupdate).isValid()
        ? moment(item[0].followupdate).format("LL")
        : "",
    );

    addSection("Diagnosis", item[0].diagnosis);

    // === SIGNATURE ===
    if (y + 40 > pageHeight - bottomMargin) {
      addNewPage();
    }

    const signatureY = Math.min(y + 35, pageHeight - 55);

    const lineWidth = 80;

    const marginRight = 24;

    const lineX = pageWidth - lineWidth - marginRight;

    const marginX = lineX + lineWidth / 2 - 15;

    const sigStartY = signatureY - 25;

    try {
      if (item[0].doctor_id === 1) {
        doc.addImage(eSignatureLee, "PNG", marginX, sigStartY, 30, 35);
      }

      if (item[0].doctor_id === 13) {
        doc.addImage(eSignatureMelanie, "PNG", marginX, sigStartY, 30, 35);
      }
    } catch (err) {
      console.warn("⚠️ Signature image could not be added:", err);
    }

    doc.setFont("helvetica", "bold");

    doc.setFontSize(13);

    doc.text(safeText(item[0].doctor_name), pageWidth - 65, signatureY, {
      align: "center",
    });

    doc.setFont("helvetica", "normal");

    doc.setFontSize(11);

    doc.text(
      `License No: ${safeText(item[0].license_number)}`,
      pageWidth - 65,
      signatureY + 7,
      {
        align: "center",
      },
    );

    doc.text(
      `PTR No: ${safeText(item[0].ptr_number)}`,
      pageWidth - 65,
      signatureY + 14,
      {
        align: "center",
      },
    );

    // === FOOTER ===
    const totalPages = doc.internal.getNumberOfPages();

    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);

      doc.setFont("helvetica", "italic");

      doc.setFontSize(10);

      doc.setTextColor(100);

      doc.text(`Page ${i}`, pageWidth / 2, pageHeight - 10, {
        align: "center",
      });
    }

    // === SAVE LOCAL PDF ===
    const transactionId = safeText(item[0].transactionid);

    doc.save(`LAB_TEST_${transactionId || Date.now()}.pdf`);
  };

  return { exportReferralPDF };
}
